import React, { useState, useCallback, useEffect } from 'react';

import { Button, DefaultSheet, SheetBody, SheetFooter, TabsList, Tabs, TabsTrigger, TabsContent } from 'twyne-new.ui';

import { useAppAlert } from '@Components/AppAlert';

import { useDynamicOptions } from '../utils';

import DynamicSortby from './DynamicSortby';
import Filter from './Filter'
import GroupCheck from './GroupCheck';
import Pivot from './Pivot';
import ReportViews from './ReportView';
import SortableFields from './SortableFields';
import { ConfigureButton } from '@/components/reports/header';

export default function HierarchicalConfigure({ store, configure, onApply, onSelect, value, suppressGroupBy }) {
  const { handleError } = useAppAlert()
  const { filterby: filterbyConf, groupby: groupbyConf, metrics: metricsConf, sortby: sortbyConf, pivotby: pivotbyConfig } = configure
  const [open, setOpen] = useState(false)
  const [filterby, setFilter] = useState((filterbyConf || {}).default)
  const [groupby, setGroupby] = useState((groupbyConf || {}).default)
  const [metrics, setMetrics] = useState((metricsConf || {}).default)
  const [sortby, setSortby] = useState((sortbyConf || {}).default)
  const [pivotby, setPivotby] = useState((pivotbyConfig || {}).default)
  const [tabs, setTabs] = useState(null)

  const [groupbyOptions, groupbyDynamicGroups] = useDynamicOptions(groupbyConf?.options, open)

  useEffect(() => { setFilter(value.filterby) }, [value.filterby])
  useEffect(() => { setGroupby(value.groupby) }, [value.groupby])
  useEffect(() => { setMetrics(value.metrics) }, [value.metrics])
  useEffect(() => { setSortby(value.sortby) }, [value.sortby])
  useEffect(() => { setPivotby(value.pivotby) }, [value.pivotby])

  useEffect(() => {
    setTabs([...Object.keys(configure)]
      .filter(key => key !== 'groupby' || !suppressGroupBy)
      .map(key => [key, TAB_LABELS[key]]))
    onApply({ groupby, sortby, metrics, filterby, pivotby })
  }, [])

  const handleClose = () => {
    setOpen(false)
  }

  const handleApply = useCallback(() => {
    if (onApply({ groupby, sortby, metrics, filterby, pivotby }))
      setOpen(false)
    else
      handleError('Error', 'Select at least one field to group.')
  }, [groupby, sortby, metrics, filterby, pivotby, handleError])

  const handleSelect = configuration => {
    const { filterby, groupby, metrics, sortby, pivotby } = configuration
    setFilter(filterby)
    setGroupby(groupby)
    setMetrics(metrics)
    setSortby(sortby)
    setPivotby(pivotby)
    onSelect(configuration)
  }

  return <>
    <DefaultSheet
      contentProps={{ className: 'w-[60%] h-[calc(100vh-10rem)] max-w-[1000px] flex flex-col gap-0 overflow-hidden' }}
      title='Configure Report'
      trigger={<ConfigureButton onClick={() => setOpen(true)} />}
      wrapperProps={{ open, onOpenChange: setOpen }}
    >
      <SheetBody className='pt-0 border-0! overflow-y-auto flex-1 min-h-0'>
        <Tabs defaultValue='filterby' size='sm'>
          <TabsList>
            {tabs?.map(([key, label]) => <TabsTrigger key={key} value={key}>{label}</TabsTrigger>)}
          </TabsList>
          {pivotbyConfig && <TabWrapper id="pivotby">
            <Pivot options={pivotbyConfig.options} onChange={setPivotby} defaultValue={pivotby} />
          </TabWrapper>}
          {filterbyConf && <TabWrapper id="filterby" className='mt-0!'>
            <Filter data={filterbyConf} onChange={setFilter} defaultValue={filterby} />
          </TabWrapper>}
          {groupbyConf && !suppressGroupBy && <TabWrapper id='groupby' title='Grouping by:' fields={groupby} onChange={setGroupby}>
            <GroupCheck fields={groupby} options={groupbyOptions} onChange={setGroupby} defaultValue={groupby} dynamicGroups={groupbyDynamicGroups} />
          </TabWrapper>}
          {sortbyConf && <TabWrapper id="sortby" title='Sorting by:' fields={sortby} onChange={setSortby}>
            <DynamicSortby options={sortbyConf.options} selectedGroupby={groupby} selectedMetrics={metrics}
              sourceGroupby={groupbyOptions} sourceMetrics={metricsConf.options}
              onChange={setSortby} defaultValue={sortby} />
          </TabWrapper>}
          {metricsConf && <TabWrapper id="metrics">
            <GroupCheck options={metricsConf.options} onChange={setMetrics} defaultValue={metrics} selectAllButton />
          </TabWrapper>}
        </Tabs>
      </SheetBody>
      <SheetFooter className='flex justify-between border-t twy-border-base'>
        <div className='flex gap-2'>
          <ReportViews store={store} onSelect={handleSelect} onApply={onApply} configuration={{ filterby, groupby, metrics, sortby, pivotby }} />
        </div>
        <div className='flex gap-2'>
          <Button size='lg' onClick={handleClose} variant='ghost'>Cancel</Button>
          <Button size='lg' onClick={handleApply} color="accent">Apply</Button>
        </div>
      </SheetFooter>
    </DefaultSheet>
  </>
}

function TabWrapper({ id, title, fields, onChange, children, className = '' }) {
  const handleRemove = idx => () => {
    fields.splice(idx, 1)
    onChange([...fields])
  }

  const handleChangeOrder = order => {
    onChange(order)
  }

  return <TabsContent value={id} className={className}>
    {fields && <div className='mb-4 border twy-border-base rounded-sm p-4'>
      <div className='text-md twy-text-base-accent'>{title}</div>
      {fields.length > 0 && <div className='mt-2'>
        <SortableFields
          fields={fields}
          onChangeOrder={handleChangeOrder}
          onRemove={handleRemove}
          id={id}
        />
      </div>}
    </div>}
    {children}
  </TabsContent >
}

const TAB_LABELS = {
  views: 'Views',
  pivotby: 'Pivot',
  filterby: 'Filter',
  groupby: 'Group By',
  sortby: 'Sort By',
  metrics: 'Metrics',
}
